import { getUsersProgressFn } from "@/actions/admin/server";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { tr } from "date-fns/locale";
import {
  Activity,
  ArrowLeft,
  Search,
  Target,
  TrendingUp,
  User,
  Users as UsersIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";

const usersSearchSchema = z.object({
  q: z.string().optional(),
  filter: z.enum(["all", "clients"]).optional(),
});

export const Route = createFileRoute("/_app/admin/users/")({
  validateSearch: usersSearchSchema,
  beforeLoad: async ({ context }) => {
    if (!context.user || context.user.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["admin", "users-progress"],
      queryFn: () => getUsersProgressFn(),
    });
  },
  component: AdminUsersPage,
});

function AdminUsersPage() {
  const { q, filter = "all" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data } = useSuspenseQuery({
    queryKey: ["admin", "users-progress"],
    queryFn: () => getUsersProgressFn(),
  });
  const users = data?.body || [];

  const [search, setSearch] = useState(q || "");

  useEffect(() => {
    setSearch(q || "");
  }, [q]);

  const handleFilterChange = (val: string) => {
    navigate({
      search: (prev) => ({ ...prev, filter: (val as any) || undefined }),
    });
  };

  const handleSearchChange = (val: string) => {
    setSearch(val);
    navigate({
      search: (prev) => ({ ...prev, q: val || undefined }),
    });
  };

  const handleRowClick = (userId: string, _userName: string) => {
    navigate({
      to: "/admin/users/$userId",
      params: { userId },
    });
  };

  const filteredUsers = users.filter((u) => {
    const searchLower = search.toLowerCase();
    const matchesSearch =
      u.userName?.toLowerCase().includes(searchLower) ||
      u.email.toLowerCase().includes(searchLower) ||
      u.activeProgram?.name?.toLowerCase().includes(searchLower);

    if (filter === "clients") {
      return matchesSearch && u.isCoachingClient;
    }
    return matchesSearch;
  });

  const stats = {
    total: users.length,
    activePrograms: users.filter((u) => u.activeProgram).length,
    totalClients: users.filter((u) => u.isCoachingClient).length,
    avgProgress:
      users.length > 0
        ? Math.round(
            users.reduce(
              (acc, u) => acc + (u.activeProgram?.currentWeek || 0),
              0,
            ) / users.length,
          )
        : 0,
  };

  return (
    <div className="container mx-auto max-w-7xl space-y-8 px-4 py-24 sm:px-6 lg:px-8">
      {/* Breadcrumb & Header */}
      <div className="flex flex-col gap-6">
        <Button asChild variant="ghost" className="w-fit">
          <Link to="/admin">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Admin Paneli
          </Link>
        </Button>

        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h1 className="font-bold text-3xl tracking-tight">
              Kullanıcı Takibi
            </h1>
            <p className="mt-1 text-muted-foreground">
              Kullanıcıların program ilerlemelerini ve aktiflik durumlarını
              izleyin
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Kullanıcı veya program ara..."
              className="pl-9"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-medium text-sm">
              Toplam Kullanıcı
            </CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl">{stats.total}</div>
          </CardContent>
        </Card>
        <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-medium text-sm">
              Aktif Danışanlar
            </CardTitle>
            <Target className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl text-primary">
              {stats.totalClients}
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-medium text-sm">
              Aktif Programlar
            </CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl">{stats.activePrograms}</div>
          </CardContent>
        </Card>
        <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-medium text-sm">Ort. İlerleme</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl">{stats.avgProgress}. Hafta</div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table Controls */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Tabs
          value={filter}
          onValueChange={handleFilterChange}
          className="w-full sm:w-auto"
        >
          <TabsList>
            <TabsTrigger value="all" className="flex gap-2">
              <UsersIcon className="h-4 w-4" />
              Hepsi
            </TabsTrigger>
            <TabsTrigger value="clients" className="flex gap-2">
              <Target className="h-4 w-4" />
              Danışanlar
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {/* Users List */}
      <Card className="overflow-hidden border border-border/60 bg-card/30 shadow-sm">
        {/* Desktop View */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow className="border-border/40 border-b hover:bg-transparent">
                <TableHead className="w-[200px] font-semibold text-muted-foreground text-xs uppercase">
                  Kullanıcı
                </TableHead>
                <TableHead className="text-center font-semibold text-muted-foreground text-xs uppercase">
                  Durum
                </TableHead>
                <TableHead className="font-semibold text-muted-foreground text-xs uppercase">
                  Program
                </TableHead>
                <TableHead className="font-semibold text-muted-foreground text-xs uppercase">
                  İlerleme
                </TableHead>
                <TableHead className="font-semibold text-muted-foreground text-xs uppercase">
                  Son Aktivite
                </TableHead>
                <TableHead className="text-right font-semibold text-muted-foreground text-xs uppercase">
                  Analiz
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="h-32 text-center text-muted-foreground"
                  >
                    <p className="text-sm">Kullanıcı bulunamadı</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow
                    key={user.userId}
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() =>
                      handleRowClick(user.userId, user.userName || "")
                    }
                  >
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium text-sm">
                          {user.userName || "İsimsiz"}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {user.email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {user.isCoachingClient ? (
                        <Badge
                          variant="secondary"
                          className="border-primary/10 bg-primary/5 font-bold text-[9px] text-primary"
                        >
                          DANIŞAN
                        </Badge>
                      ) : (
                        <span className="font-medium text-[10px] text-muted-foreground uppercase">
                          Standart
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-xs">
                        {user.activeProgram?.name || "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {user.activeProgram ? (
                        <div className="flex items-center gap-2">
                          <div className="h-1 w-12 rounded-full bg-muted">
                            <div
                              className="h-full bg-primary"
                              style={{
                                width: `${((user.activeProgram?.currentWeek || 0) / (user.activeProgram?.totalWeeks || 1)) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="font-medium text-[10px] tabular-nums">
                            {user.activeProgram?.currentWeek}/
                            {user.activeProgram?.totalWeeks}
                          </span>
                        </div>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-[11px] text-muted-foreground">
                        {user.lastActiveDate
                          ? formatDistanceToNow(new Date(user.lastActiveDate), {
                              addSuffix: true,
                              locale: tr,
                            })
                          : "-"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {renderStatusBadge(user.lastActiveDate)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Mobile View - Simple List */}
        <div className="divide-y divide-border/40 md:hidden">
          {filteredUsers.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground text-sm">
              Kullanıcı bulunamadı
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div
                key={user.userId}
                className="flex items-center justify-between gap-3 p-4 transition-colors active:bg-muted/50"
                onClick={() => handleRowClick(user.userId, user.userName || "")}
              >
                <div className="flex min-w-0 flex-col">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold text-sm">
                      {user.userName || "İsimsiz"}
                    </span>
                    {user.isCoachingClient && (
                      <div className="h-1 w-1 rounded-full bg-primary" />
                    )}
                  </div>
                  <span className="truncate text-[11px] text-muted-foreground">
                    {user.email}
                  </span>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  {renderStatusBadge(user.lastActiveDate)}
                  <span className="text-[10px] text-muted-foreground">
                    {user.lastActiveDate
                      ? formatDistanceToNow(new Date(user.lastActiveDate), {
                          addSuffix: true,
                          locale: tr,
                        })
                      : "İşlem yok"}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}

function renderStatusBadge(lastActiveDate: string | Date | null) {
  if (!lastActiveDate) {
    return (
      <Badge variant="outline" className="font-medium text-[9px] opacity-40">
        VERİ YOK
      </Badge>
    );
  }

  const daysSinceActive =
    (new Date().getTime() - new Date(lastActiveDate).getTime()) /
    (1000 * 3600 * 24);

  if (daysSinceActive <= 3) {
    return (
      <Badge
        variant="outline"
        className="border-green-500/30 bg-green-500/5 font-bold text-[9px] text-green-600"
      >
        AKTİF
      </Badge>
    );
  }
  if (daysSinceActive <= 7) {
    return (
      <Badge
        variant="outline"
        className="border-yellow-500/30 bg-yellow-500/5 font-bold text-[9px] text-yellow-600"
      >
        RİSKLİ
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="border-destructive/30 bg-destructive/5 font-bold text-[9px] text-destructive"
    >
      PASİF
    </Badge>
  );
}
